class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  /* THE DRAWER HOLDS ITS OWN SCROLL LOCK.

     .overflow-hidden on the body is shared by five files in this theme — the
     drawer, the mega panel, details-modal, ModalDialog and pickup-availability
     — and none of them tracks who owns it. Whoever runs last wins, so anything
     that closes shortly after the drawer opens takes the lock off with it and
     leaves the drawer sitting over a page that still scrolls.

     Chasing each of those in turn is whack-a-mole, and a third-party script can
     do the same thing without being in the theme at all. So instead of trusting
     the class to stay put, the drawer watches it and puts it back for as long
     as the drawer is open.

     Re-adding the class mutates it again and re-enters the callback once, where
     the class is now present and nothing happens. It does not loop. The
     observer exists only while the drawer is open. */
  lockScroll() {
    this.scrollLocked = true;
    document.body.classList.add('overflow-hidden');

    if (this.scrollLockObserver || typeof MutationObserver !== 'function') return;

    this.scrollLockObserver = new MutationObserver(() => {
      if (!this.scrollLocked) return;
      if (!document.body.classList.contains('overflow-hidden')) {
        document.body.classList.add('overflow-hidden');
      }
    });

    this.scrollLockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  unlockScroll() {
    // Cleared first, or the observer would immediately undo the removal below.
    this.scrollLocked = false;

    if (this.scrollLockObserver) {
      this.scrollLockObserver.disconnect();
      this.scrollLockObserver = null;
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* OPENING THE DRAWER MUST NOT DEPEND ON THE REFRESH SUCCEEDING.

     Every step below used to run unguarded, so a single throw anywhere in
     here aborted the method before the setTimeout that calls open(). The
     drawer was left populated but never opened, and — because open() is also
     what adds .overflow-hidden to the body — the page went on scrolling
     behind it.

     getSectionInnerHTML is the likeliest thrower: it does
     querySelector(selector).innerHTML with no null check, so any section that
     comes back without its expected wrapper takes the whole open with it. The
     failure is per-page, since sections are rendered against sections_url.

     Each step is now isolated. A section that cannot be rendered is logged
     and skipped, and the drawer still opens. */
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? this.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const html = parsedState.sections && parsedState.sections[section.id];
      if (!html) return;

      try {
        sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
      } catch (e) {
        console.error(`[cart-drawer] could not render section "${section.id}"`, e);
      }
    });

    // Optional add-ons. Never allowed to hold the drawer shut.
    try {
      this.refreshCartWidgets();
    } catch (e) {
      console.error('[cart-drawer] cart widget refresh failed', e);
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  refreshCartWidgets() {
    if (typeof window.__freeShippingBarInit === 'function') {
      window.__freeShippingBarInit();
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    // Drawer is refreshed via cart-drawer.renderContents() on add-to-cart and
    // updateQuantity() for line changes. Do not inject main-cart-items HTML here.
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
